/**
 * CCDB BFF API
 * Backend for Frontend API for CCDB collection app.
 *
 * NOTE: This class is generated following OpenAPI Generator patterns.
 */

import {createAction} from '@reduxjs/toolkit';
import {ApiMethods, ConcurrentStrategies} from '@pb/middleware-api';
import {PayloadData} from '../types';
import type {CcdbCompany} from '../models/CcdbCompany';
import type {CompanySearchResponse} from '../models/CompanySearchResponse';
import type {CcdbError} from '../models/CcdbError';

// ============================================
// Search Companies
// ============================================

export interface SearchCompanies$Request {
    searchValue: string;
    searchField: 'COMPANY_NAME' | 'COMPANY_ID' | 'SHARECLASS_ID' | 'COMPANY_CIK' | 'SYMBOL' | 'COY_ID';
    page?: number;
    size?: number;
}

export interface SearchCompanies$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const searchCompaniesRequest = createAction<PayloadData<void, SearchCompanies$Request>>('CCDB_BFF__SEARCH_COMPANIES__REQUEST');
export const searchCompaniesSuccess = createAction<PayloadData<CompanySearchResponse, SearchCompanies$Request>>('CCDB_BFF__SEARCH_COMPANIES__SUCCESS');
export const searchCompaniesFailure = createAction<PayloadData<CcdbError, SearchCompanies$Request, true>>('CCDB_BFF__SEARCH_COMPANIES__FAILURE');

export const searchCompaniesActions = [
    searchCompaniesRequest,
    searchCompaniesSuccess,
    searchCompaniesFailure,
] as const;

/**
 * Search for companies
 * @param {string} meta.searchValue - Value to search for
 * @param {string} meta.searchField - Field to search in (COMPANY_NAME, COMPANY_ID, etc.)
 * @param {number} [meta.page=0] - Page number
 * @param {number} [meta.size=20] - Page size
 */
export const searchCompanies = (meta: SearchCompanies$Request, config?: SearchCompanies$Config) => {
    const queryParams = new URLSearchParams();
    queryParams.set('searchValue', meta.searchValue);
    queryParams.set('searchField', meta.searchField);
    if (meta.page !== undefined) queryParams.set('page', String(meta.page));
    if (meta.size !== undefined) queryParams.set('size', String(meta.size));

    return {
        CALL_API: {
            types: searchCompaniesActions,
            method: ApiMethods.GET,
            endpoint: `/api/companies/search?${queryParams.toString()}`,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            },
        },
        ...meta,
    };
};

// ============================================
// Get Company By ID
// ============================================

export interface GetCompanyById$Request {
    companyId: string;
}

export interface GetCompanyById$Config {
    bulk?: boolean;
    concurrentStrategy?: typeof ConcurrentStrategies[keyof typeof ConcurrentStrategies];
    signal?: AbortSignal;
}

export const getCompanyByIdRequest = createAction<PayloadData<void, GetCompanyById$Request>>('CCDB_BFF__GET_COMPANY_BY_ID__REQUEST');
export const getCompanyByIdSuccess = createAction<PayloadData<CcdbCompany, GetCompanyById$Request>>('CCDB_BFF__GET_COMPANY_BY_ID__SUCCESS');
export const getCompanyByIdFailure = createAction<PayloadData<CcdbError, GetCompanyById$Request, true>>('CCDB_BFF__GET_COMPANY_BY_ID__FAILURE');

export const getCompanyByIdActions = [
    getCompanyByIdRequest,
    getCompanyByIdSuccess,
    getCompanyByIdFailure,
] as const;

/**
 * Get company by ID
 * @param {string} meta.companyId - The company ID
 */
export const getCompanyById = (meta: GetCompanyById$Request, config?: GetCompanyById$Config) => {
    return {
        CALL_API: {
            types: getCompanyByIdActions,
            method: ApiMethods.GET,
            endpoint: `/api/companies/${encodeURIComponent(meta.companyId)}`,
            bulk: config?.bulk,
            concurrentStrategy: config?.concurrentStrategy,
            signal: config?.signal,
            headers: {
                'Accept': 'application/json',
            },
        },
        ...meta,
    };
};
